import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { MapPin, MessageCircle, Mail, Sparkles, ShieldCheck } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";
import { buildWhatsappUrl } from "@/lib/whatsapp";

export const Footer = () => {
  const { t } = useTranslation("footer");
  const year = new Date().getFullYear();

  return (
    <footer className="relative mt-16 overflow-hidden border-t border-primary/15 bg-background sm:mt-24">
      {/* Halos aurora */}
      <div
        aria-hidden
        className="pointer-events-none absolute -top-32 left-1/4 size-[420px] rounded-full opacity-40 blur-3xl"
        style={{ background: "radial-gradient(closest-side, hsl(var(--primary) / 0.55), transparent 70%)" }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-40 right-1/4 size-[420px] rounded-full opacity-35 blur-3xl"
        style={{ background: "radial-gradient(closest-side, hsl(var(--aurora-violet) / 0.55), transparent 70%)" }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-px"
        style={{
          background:
            "linear-gradient(90deg, transparent, hsl(var(--aurora-cyan) / 0.5), hsl(var(--primary) / 0.5), hsl(var(--aurora-violet) / 0.5), transparent)",
        }}
      />
      {/* Grain organique */}
      <div aria-hidden className="bg-grain pointer-events-none absolute inset-0" />

      <div className="container relative grid gap-8 py-12 sm:gap-10 sm:py-16 md:grid-cols-2 lg:grid-cols-5">
        <div className="space-y-4 lg:col-span-1">
          <BrandLogo />
          <p className="max-w-xs text-sm leading-relaxed text-muted-foreground">
            {t("contact.city")}
          </p>
          <div className="inline-flex items-center gap-1.5 rounded-full border border-primary/25 bg-primary/10 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-primary backdrop-blur">
            <Sparkles className="size-3" />
            Aurora 2026
          </div>
        </div>

        <div className="glass relative rounded-2xl p-5">
          <h3 className="mb-4 text-xs font-bold uppercase tracking-wider text-aurora">
            {t("columns.explore")}
          </h3>
          <ul className="space-y-2.5 text-sm">
            <li><Link to="/services" className="text-muted-foreground transition-colors hover:text-primary">{t("links.services")}</Link></li>
            <li><Link to="/services/industripub" className="text-muted-foreground transition-colors hover:text-primary">IndustriPub</Link></li>
            <li><Link to="/services/digitalpub" className="text-muted-foreground transition-colors hover:text-primary">DigitalPub</Link></li>
            <li><Link to="/services/printpub" className="text-muted-foreground transition-colors hover:text-primary">PrintPub</Link></li>
            <li><Link to="/services/3design" className="text-muted-foreground transition-colors hover:text-primary">3Design</Link></li>
            <li><Link to="/services/dev" className="text-muted-foreground transition-colors hover:text-primary">Dev On Demand</Link></li>
            <li><Link to="/packs" className="text-muted-foreground transition-colors hover:text-primary">{t("links.packs")}</Link></li>
            <li><Link to="/micro-services" className="text-muted-foreground transition-colors hover:text-primary">{t("links.microservices")}</Link></li>
          </ul>
        </div>

        <div className="glass relative rounded-2xl p-5">
          <h3 className="mb-4 text-xs font-bold uppercase tracking-wider text-aurora">
            Maroc
          </h3>
          <ul className="space-y-2.5 text-sm">
            <li><Link to="/maroc/casablanca" className="text-muted-foreground transition-colors hover:text-primary">Casablanca</Link></li>
            <li><Link to="/maroc/rabat" className="text-muted-foreground transition-colors hover:text-primary">Rabat</Link></li>
            <li><Link to="/maroc/marrakech" className="text-muted-foreground transition-colors hover:text-primary">Marrakech</Link></li>
            <li><Link to="/maroc/tanger" className="text-muted-foreground transition-colors hover:text-primary">Tanger</Link></li>
            <li><Link to="/maroc/agadir" className="text-muted-foreground transition-colors hover:text-primary">Agadir</Link></li>
            <li><Link to="/plan-du-site" className="text-muted-foreground transition-colors hover:text-primary">Plan du site</Link></li>
            <li><Link to="/blog" className="text-muted-foreground transition-colors hover:text-primary">{t("links.blog")}</Link></li>
          </ul>
        </div>

        <div className="glass relative rounded-2xl p-5">
          <h3 className="mb-4 text-xs font-bold uppercase tracking-wider text-aurora">
            {t("columns.contact")}
          </h3>
          <ul className="space-y-3 text-sm">
            <li className="flex items-start gap-2 text-muted-foreground">
              <span className="mt-0.5 grid size-7 shrink-0 place-items-center rounded-full bg-primary/15 text-primary">
                <MapPin className="size-3.5" />
              </span>
              <span>{t("contact.city")}</span>
            </li>
            <li>
              <a
                href={buildWhatsappUrl("Salam, je viens de Conceptions.ma 👋")}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start gap-2 text-muted-foreground transition-colors hover:text-success"
              >
                <span className="mt-0.5 grid size-7 shrink-0 place-items-center rounded-full bg-success/15 text-success">
                  <MessageCircle className="size-3.5" />
                </span>
                <span>{t("contact.whatsapp")}</span>
              </a>
            </li>
            <li>
              <a
                href={`mailto:${t("contact.email")}`}
                className="flex items-start gap-2 text-muted-foreground transition-colors hover:text-secondary"
              >
                <span className="mt-0.5 grid size-7 shrink-0 place-items-center rounded-full bg-secondary/15 text-secondary">
                  <Mail className="size-3.5" />
                </span>
                <span>{t("contact.email")}</span>
              </a>
            </li>
            <li>
              <Link to="/devis" className="text-muted-foreground transition-colors hover:text-primary">{t("links.quote")}</Link>
            </li>
          </ul>
        </div>

        <div className="glass relative rounded-2xl p-5">
          <h3 className="mb-4 text-xs font-bold uppercase tracking-wider text-aurora">
            {t("columns.legal")}
          </h3>
          <ul className="space-y-2.5 text-sm">
            <li><Link to="/a-propos" className="text-muted-foreground transition-colors hover:text-primary">{t("links.about")}</Link></li>
            <li><Link to="/partenaires" className="text-muted-foreground transition-colors hover:text-primary">Partenaires</Link></li>
            <li><Link to="/cgv" className="text-muted-foreground transition-colors hover:text-primary">{t("links.terms")}</Link></li>
            <li><Link to="/confidentialite" className="text-muted-foreground transition-colors hover:text-primary">{t("links.privacy")}</Link></li>
            <li><Link to="/mentions-legales" className="text-muted-foreground transition-colors hover:text-primary">{t("links.legal")}</Link></li>
          </ul>
        </div>
      </div>

      <div className="relative border-t border-border/60 safe-bottom">
        <div className="container flex flex-col items-center justify-between gap-2 py-5 text-xs text-muted-foreground sm:flex-row">
          <p>© {year} <span className="font-display font-semibold text-foreground">Conceptions.ma</span> — {t("rights")}</p>
          <div className="flex items-center gap-3 opacity-90">
            <p>{t("powered")}</p>
            <span className="opacity-40">·</span>
            <Link
              to="/admin/login"
              className="inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/10 px-3 py-1.5 text-[11px] font-semibold text-primary transition-all hover:border-primary/60 hover:bg-primary/20"
              aria-label="Connexion équipe — espace administration"
            >
              <ShieldCheck className="size-3.5" />
              Connexion équipe
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};
